
import React, {
    useEffect,
    useRef,
    useState
} from 'react';

import {
    useDispatch,
    useSelector
} from 'react-redux';

import Swal from 'sweetalert2';

import ModalAdendaAnamnesis from './ModalAdendaAnamnesis';

import {
    solicitarAnamnesisEntrevistaAction,
    almacenarAnamnesisEntrevistaAction
} from '../../actions/anamnesisEntrevistaActions';

import {
    FlexBox
} from '../Css/StyledHeader.js';

import {
    Fondo,
    Margen,
    Label,
    Input,
    BotonSmall
} from '../Css/StyledAnamnesis.js';


/*
====================================
FECHA PARA INPUT DATETIME-LOCAL
====================================

Convertimos la fecha guardada en la base
al horario local para mostrarla en el campo.
====================================
*/

const fechaParaInput = (valor) => {

    if (!valor) {
        return '';
    }

    const fecha = new Date(valor);

    if (Number.isNaN(fecha.getTime())) {
        return '';
    }

    const dosDigitos = (numero) =>
        String(numero).padStart(2, '0');

    return (
        `${fecha.getFullYear()}-` +
        `${dosDigitos(fecha.getMonth() + 1)}-` +
        `${dosDigitos(fecha.getDate())}T` +
        `${dosDigitos(fecha.getHours())}:` +
        `${dosDigitos(fecha.getMinutes())}`
    );

};


/*
====================================
ENTREVISTA SEMIESTRUCTURADA
====================================
*/

const AnamnesisEntrevista = ({
    config,
    id_paciente
}) => {

    const dispatch = useDispatch();

    const [fechaEntrevista, setFechaEntrevista] =
        useState('');

    const [presentes, setPresentes] =
        useState('');

    const [notas, setNotas] =
        useState('');


    /*
    ====================================
    MODAL DE ADENDA - SEMIESTRUCTURADA
    ====================================

    Conservamos los datos de la entrevista
    que el profesional intentó guardar.

    No se reemplazan por otros datos
    mientras el modal esté abierto.
    ====================================
    */

    const [
        entrevistaPendiente,
        setEntrevistaPendiente
    ] = useState(null);

    const [
        guardandoConAdenda,
        setGuardandoConAdenda
    ] = useState(false);


    /*
    ====================================
    DATOS DE REDUX
    ====================================
    */

    const {
        info_anamnesis_entrevista: entrevista,
        anamnesis_entrevista_cargando: cargando,
        anamnesis_entrevista_cargada: cargada,
        anamnesis_entrevista_guardando: guardando,
        anamnesis_entrevista_error: error
    } = useSelector(
        state => state.informacion
    );


    /*
    ====================================
    CONSULTAR ENTREVISTA DEL PACIENTE
    ====================================
    */

    const pacienteConsultadoRef =
        useRef(null);


    useEffect(() => {

        if (!config || !id_paciente) {
            return;
        }

        if (
            pacienteConsultadoRef.current ===
            id_paciente
        ) {
            return;
        }

        pacienteConsultadoRef.current =
            id_paciente;


        /*
        Si cambia el paciente, limpiamos
        los campos locales antes de cargar
        su entrevista.
        */

        setFechaEntrevista('');
        setPresentes('');
        setNotas('');


        /*
        Si cambia el paciente, descartamos
        cualquier guardado pendiente del
        paciente anterior.
        */

        setEntrevistaPendiente(null);


        dispatch(
            solicitarAnamnesisEntrevistaAction({
                config,
                payload: {
                    id_paciente
                }
            })
        );

    }, [config, id_paciente, dispatch]);


    /*
    ====================================
    COMPLETAR CAMPOS CON DATOS GUARDADOS
    ====================================

    Si todavía no existe una entrevista,
    el backend devuelve null y dejamos
    los campos vacíos.
    ====================================
    */

    useEffect(() => {

        if (!cargada) {
            return;
        }

        setFechaEntrevista(
            fechaParaInput(
                entrevista?.fecha_entrevista
            )
        );

        setPresentes(
            entrevista?.presentes || ''
        );

        setNotas(
            entrevista?.notas || ''
        );

    }, [cargada, entrevista]);


    /*
    ====================================
    GUARDAR ENTREVISTA
    ====================================
    */

    const guardarEntrevista = async () => {

        /*
        No iniciamos otro guardado mientras
        haya una petición en curso o el modal
        esté esperando una confirmación.
        */

        if (
            !id_paciente ||
            guardando ||
            entrevistaPendiente
        ) {
            return;
        }


        /*
        ====================================
        VALIDAR FECHA Y HORA
        ====================================
        */

        if (!fechaEntrevista) {

            await Swal.fire({

                title: 'Falta completar la fecha',

                text:
                    'Indicá la fecha y hora de la entrevista.',

                icon: 'warning',

                confirmButtonText: 'Aceptar',

                confirmButtonColor: '#23AAAF'

            });

            return;

        }


        /*
        ====================================
        VALIDAR CONTENIDO
        ====================================
        */

        if (
            !presentes.trim() &&
            !notas.trim()
        ) {

            await Swal.fire({

                title: 'Falta completar la entrevista',

                text:
                    'Completá los presentes o las notas de la entrevista.',

                icon: 'warning',

                confirmButtonText: 'Aceptar',

                confirmButtonColor: '#23AAAF'

            });

            return;

        }


        /*
        Enviamos la fecha con zona horaria
        explícita, como espera el backend.
        */

        const fechaISO =
            new Date(fechaEntrevista).toISOString();


        /*
        ====================================
        PREPARAR DATOS DE LA ENTREVISTA
        ====================================

        Guardamos una copia del contenido
        que el profesional intentó enviar.

        Si se necesita una adenda, usaremos
        esta misma copia al confirmar el modal.
        ====================================
        */

        const informacion = {

            config,

            payload: {

                id_paciente,

                fecha_entrevista: fechaISO,

                presentes: presentes.trim(),

                notas: notas.trim()

            }

        };


        /*
        ====================================
        INTENTAR GUARDAR ENTREVISTA
        ====================================
        */

        const resultado = await dispatch(
            almacenarAnamnesisEntrevistaAction(
                informacion
            )
        );


        /*
        ====================================
        ABRIR MODAL SI SE REQUIERE ADENDA
        ====================================
        */

        if (resultado?.requiere_adenda) {

            setEntrevistaPendiente(
                informacion
            );

        }

    };


    /*
    ====================================
    CANCELAR ADENDA
    ====================================

    Cerramos el modal sin guardar.

    Los campos de la entrevista conservan
    el contenido que escribió el profesional.
    ====================================
    */

    const cancelarAdendaEntrevista = () => {

        if (guardandoConAdenda) {
            return;
        }

        setEntrevistaPendiente(null);

    };


    /*
    ====================================
    CONFIRMAR ADENDA Y GUARDAR ENTREVISTA
    ====================================

    Enviamos la entrevista pendiente y
    su adenda en una única petición.

    El backend se encarga de guardarlas
    juntas dentro de una transacción.
    ====================================
    */

    const confirmarAdendaEntrevista = async (
        adenda
    ) => {

        if (
            !entrevistaPendiente ||
            guardandoConAdenda
        ) {
            return;
        }


        /*
        Evitamos confirmar información de
        un paciente distinto del seleccionado.
        */

        if (
            entrevistaPendiente.payload.id_paciente !==
            id_paciente
        ) {

            setEntrevistaPendiente(null);

            return;

        }


        setGuardandoConAdenda(true);


        try {

            const resultado = await dispatch(
                almacenarAnamnesisEntrevistaAction({

                    config:
                        entrevistaPendiente.config,

                    payload: {

                        ...entrevistaPendiente.payload,

                        adenda

                    }

                })
            );


            /*
            Solo cerramos el modal cuando el
            backend confirma el guardado.

            Si ocurre un error, el texto de
            la adenda permanece en pantalla.
            */

            if (resultado?.ok) {

                setEntrevistaPendiente(null);

            }

        } finally {

            setGuardandoConAdenda(false);

        }

    };


    /*
    ====================================
    REINTENTAR CONSULTA
    ====================================
    */

    const reintentarConsulta = () => {

        if (
            !config ||
            !id_paciente ||
            cargando
        ) {
            return;
        }

        dispatch(
            solicitarAnamnesisEntrevistaAction({
                config,
                payload: {
                    id_paciente
                }
            })
        );

    };


    return (

        <Fondo>

            {/* =====================================
                CONTENIDO DE LA ENTREVISTA
            ======================================

                La sección ya se selecciona desde
                los botones superiores de
                Anamnesis.js.

                Mostramos sus campos directamente.
            ====================================== */}

            <div>

                <Margen>

                    {
                        cargando && (

                            <p>
                                Cargando entrevista...
                            </p>

                        )
                    }


                    {
                        !cargando &&
                        !cargada &&
                        error && (

                            <div>

                                <p>
                                    No se pudo cargar
                                    la entrevista.
                                </p>

                                <button
                                    type="button"
                                    onClick={
                                        reintentarConsulta
                                    }
                                >
                                    Reintentar
                                </button>

                            </div>

                        )
                    }


                    {
                        cargada && (

                            <>

                                {/* FECHA Y HORA */}

                                <FlexBox>

                                    <Label
                                        htmlFor="fechaEntrevista"
                                    >
                                        Fecha y hora
                                    </Label>

                                    <Input
                                        id="fechaEntrevista"
                                        name="fechaEntrevista"
                                        type="datetime-local"
                                        value={
                                            fechaEntrevista
                                        }
                                        onChange={e =>
                                            setFechaEntrevista(
                                                e.target.value
                                            )
                                        }
                                    />

                                </FlexBox>


                                {/* PRESENTES */}

                                <FlexBox>

                                    <Label
                                        htmlFor="presentesEntrevista"
                                    >
                                        Presentes
                                    </Label>

                                    <Input
                                        id="presentesEntrevista"
                                        name="presentesEntrevista"
                                        type="text"
                                        placeholder={
                                            'Personas presentes durante la entrevista'
                                        }
                                        value={presentes}
                                        onChange={e =>
                                            setPresentes(
                                                e.target.value
                                            )
                                        }
                                    />

                                </FlexBox>


                                {/* NOTAS */}

                                <FlexBox>

                                    <Label
                                        htmlFor="notasEntrevista"
                                    >
                                        Notas de la entrevista
                                    </Label>

                                    <Input
                                        as="textarea"
                                        id="notasEntrevista"
                                        name="notasEntrevista"
                                        rows={8}
                                        value={notas}
                                        onChange={e =>
                                            setNotas(
                                                e.target.value
                                            )
                                        }
                                        style={{
                                            minHeight: '180px',
                                            resize: 'vertical'
                                        }}
                                        placeholder={
                                            'Escribí aquí las notas de la entrevista...'
                                        }
                                    />

                                </FlexBox>


                                {/* GUARDAR ENTREVISTA */}

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        marginTop: '18px'
                                    }}
                                >

                                    <BotonSmall
                                        type="button"
                                        onClick={
                                            guardarEntrevista
                                        }
                                        disabled={
                                            guardando ||
                                            guardandoConAdenda
                                        }
                                    >

                                        {
                                            guardando
                                                ? 'Guardando...'
                                                : 'Guardar entrevista'
                                        }

                                    </BotonSmall>

                                </div>

                            </>

                        )
                    }

                </Margen>

            </div>


            {/* =====================================
                MODAL DE ADENDA
                ENTREVISTA SEMIESTRUCTURADA
            ====================================== */}

            {
                entrevistaPendiente && (

                    <ModalAdendaAnamnesis

                        tipoDocumento="semiestructurada"

                        guardando={guardandoConAdenda}

                        onCancelar={
                            cancelarAdendaEntrevista
                        }

                        onConfirmar={
                            confirmarAdendaEntrevista
                        }

                    />

                )
            }

        </Fondo>

    );

};


export default AnamnesisEntrevista;